// src/hooks/useMapMarkerData.tsx
import { useState, useEffect, useMemo } from "react"
import type { MarkerData } from "@/contexts/MapMarkersContext"
import { useMapMarkers } from "@/contexts/MapMarkersContext"

type UseMapMarkerDataProps<T> = {
  data: T[]
  getMarkerData: (item: T) => MarkerData
  deps?: any[]
}

export function useMapMarkerData<T>({
  data,
  getMarkerData,
  deps = [],
}: UseMapMarkerDataProps<T>) {
  const { addMarker, removeMarker, mapInstance } = useMapMarkers() // mapInstance 구조 분해
  const [lastDataIds, setLastDataIds] = useState<Set<string>>(new Set())

  // 데이터를 마커로 변환
  const markers = useMemo(() => {
    // 중요: getMarkerData가 지도 SDK 객체(예: new window.naver.maps.LatLng)에 의존하는 경우,
    // useMapMarkerData를 호출하는 컴포넌트(예: MapMarkerLayer)는
    // SDK가 로드될 때까지 `data`가 비어 있거나 getMarkerData를 안전하게 호출할 수 있도록 보장해야 합니다.
    // 예를 들어, data: sdkReady ? actualData : [] 와 같이 전달합니다.
    return data.map(item => getMarkerData(item))
  }, [data, getMarkerData, ...deps])

  // 마커 관리
  useEffect(() => {
    // 보호 구문: 컨텍스트에서 mapInstance가 사용 가능한 경우에만 진행합니다.
    if (!mapInstance) {
      console.log(
        "useMapMarkerData: mapInstance 준비 안 됨, 마커 업데이트 건너뜀.",
      )
      return // 지도가 준비되지 않았으면 아무것도 하지 않음
    }
    console.log("마커 업데이트 중")

    // 현재 데이터의 ID 집합
    const currentIds = new Set(markers.map(marker => marker.id))

    // 삭제된 마커 제거
    lastDataIds.forEach(id => {
      if (!currentIds.has(id)) {
        removeMarker(id)
        console.log(`ID ${id} 마커 제거됨`)
      }
    })

    // 새로운/업데이트된 마커 추가
    markers.forEach(marker => {
      addMarker(marker)
    })

    // 현재 ID 집합 저장
    setLastDataIds(currentIds)

    return () => {
      if (!mapInstance) return // 정리 함수도 보호
      console.log("마커 정리 중")
      // 컴포넌트 언마운트 시 마커 제거
      // currentIds가 이 effect 인스턴스에 의해 관리되는 마커를 반영하는지 확인
      const idsToRemove = new Set(markers.map(marker => marker.id))
      console.log("정리할 마커 ID:", Array.from(idsToRemove))
      idsToRemove.forEach(id => {
        removeMarker(id)
      })
    }
    // 의존성 배열에 mapInstance를 추가합니다.
    // 컨텍스트의 addMarker 및 removeMarker 함수도 안정적이거나
    // 의존하는 mapInstance가 변경되면 식별자가 변경되어야 합니다.
  }, [markers, addMarker, removeMarker, mapInstance]) // mapInstance 추가됨

  return { markers }
}
